import type { Dispatch } from 'redux';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
    CreateMarketUpdateType,
    MarketingLeadObjectType,
    UpdateSubscriptionType
} from 'app/shared/types/Marketing';

import requester, { createRequestHeaders } from '@zg-rentals/particles-js-requester';

import AppActions from 'app/shared/flux/actions/AppActions';
import StringConstants from 'app/shared/constants/StringConstants';

export const client = requester({
    proxyPath: StringConstants.PROXY_PATH,
    api: typeof window === 'undefined' ? process.env.ZG_API : '',
    logger: log.child('requester')
});

const growthClient = requester({
    proxyPath: StringConstants.RG_PROXY_PATH,
    api: typeof window === 'undefined' ? process.env.RG_API : '',
    logger: log.child('growthRequester'),
    headers: {
        'Content-Type': 'text/plain'
    }
});

const noProxyClient = requester({
    api: typeof window === 'undefined' ? process.env.ZG_API : '',
    logger: log.child('noProxyRequester')
});

const parseLogger = log.child('parseApiResponse');

const wrapDispatch = <ResponseType>(
    request: AxiosInstance['get' | 'post' | 'put' | 'delete'],
    endpoint: string,
    config: AxiosRequestConfig = {}
) => {
    // redux state hasn't been converted to ts yet
    return async (dispatch: Dispatch, getState: () => any): Promise<ResponseType> => {
        const state = getState();
        const requestConfig = {
            ...config,
            headers: {
                ...createRequestHeaders({
                    cookies: state.app.originalServerSideRequestCookies,
                    ip: state.user.ip,
                    userAgent: state.app.userAgent,
                    brand: StringConstants.ZILLOW_BRAND
                }),
                'x-csrf-token': state.app.csrfToken
            }
        };

        parseLogger.info(requestConfig, 'request config');

        const response = await request(
            endpoint,
            config.data ? config.data : requestConfig,
            config.data ? requestConfig : undefined
        );

        parseLogger.info(
            {
                status: response.status,
                statusText: response.statusText,
                data: response.data
            },
            'received api response'
        );

        if (response.headers['set-cookie']) {
            dispatch(
                AppActions.updateApp({
                    apiResponseServerSideRequestCookies: response.headers['set-cookie']
                })
            );
        }

        if (response.data.response) {
            return response.data.response;
        }

        return response.data;
    };
};

const api = {
    address: {
        autocomplete(partialAddress: string) {
            return wrapDispatch(client.get, 'rental-manager-api/api/v1/properties/addresses/autocomplete', {
                params: {
                    partialAddress
                },
                timeout: 5000
            });
        },
        getListingForAddress(fullAddress: string, unit: string) {
            return wrapDispatch(client.get, 'rental-manager-api/api/v1/users/properties/listing/getListingForAddress', {
                params: {
                    fullAddress,
                    unit
                },
                timeout: 5000
            });
        },
        graphQlListing(data: string) {
            return wrapDispatch(growthClient.post, 'rental-growth-api/api/graphql/v1/listing', {
                data
            });
        }
    },
    area: {
        autocomplete(partial: string, types?: Array<string>) {
            return wrapDispatch(growthClient.get, 'rental-growth-api/api/v1/area/autoComplete', {
                params: {
                    partial,
                    ...(types && { types })
                },
                timeout: 5000
            });
        },
        graphQlMarketData(data: string) {
            return wrapDispatch(growthClient.post, 'rental-growth-api/api/graphql/v1/marketPages', {
                data
            });
        }
    },
    analytics: {
        comscore: () => wrapDispatch(noProxyClient.get, 'rental-manager/rental-growth/comscore')
    },
    marketing: {
        createMarketingLead(params: MarketingLeadObjectType) {
            return wrapDispatch(growthClient.get, 'rental-growth-api/api/v1/marketing/marketingLead/create', {
                params
            });
        },
        createMarketUpdate(data: CreateMarketUpdateType) {
            return wrapDispatch(growthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/create', {
                data: JSON.stringify(data)
            });
        },
        updateSubscription(data: UpdateSubscriptionType) {
            return wrapDispatch(growthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/updateStatus', {
                data: JSON.stringify(data)
            });
        }
    },
    user: {
        info: (queryParams: unknown) =>
            wrapDispatch(client.get, 'rental-manager-api/api/v1/users/get', {
                params: queryParams
            })
    }
};

export default api;
